import type { SvdNativeCardVariant } from '@src/types/svd-product-card.types'

/**
 * The anchor-generic (VDA) analytics keys shared by every SVD card event (click, impression,
 * variant-select, wishlist add/remove). SVD generalizes the swatch dimension beyond colour, so the
 * legacy hardcoded `color` key alone is ambiguous for a non-colour anchor (`color:'14K'` could be a
 * metalCarat). These keys let each event self-describe its anchor:
 *
 *  - `display_config` — the swatch dimension: `{ name, value }`, e.g. `{ name:'color', value:'Brown' }`
 *    or `{ name:'metalCarat', value:'14K' }` (from `displayConfig.name` / `displayConfig.value`).
 *  - `selected_attributes` — the SKU-scoping attribute set, forwarded verbatim from the wire
 *    (no casing normalization — client policy is to forward the backend value as-is).
 *
 * Purely additive to the existing `color` keys, so downstream consumers are unaffected. Both keys are
 * omitted for a no-VDA variant (null `displayConfig` / `selectedAttributes`). This is the single seam
 * every event spreads — a new card event stays VDA-generic by adding one call, no per-event colour
 * logic.
 */
export interface SvdAnchorAnalyticsFields {
  display_config?: { name: string; value: string }
  selected_attributes?: Record<string, string>
}

export function getSvdAnchorAnalyticsFields(variant?: SvdNativeCardVariant): SvdAnchorAnalyticsFields {
  const displayConfig = variant?.displayConfig
  return {
    ...(displayConfig ? { display_config: { name: displayConfig.name, value: displayConfig.value } } : {}),
    ...(variant?.selectedAttributes ? { selected_attributes: variant.selectedAttributes } : {}),
  }
}
